import map from 'lodash/map';
import isNumber from 'lodash/isNumber';
import loEach from 'lodash/each';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
// TODO: remove jquery from here and from webpack config once CONFDEV-52616 is done.
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';
import { getRte } from '../facades/editor';
import window from 'window-or-global';

// TODO: Remove the dependency on this deprecated 3.x API
class Dispatcher {
	/**
	 * Constructs a new event dispatcher object.
	 *
	 * @constructor
	 * @method Dispatcher
	 * @param {Object} scope Optional default execution scope for all observer functions.
	 */
	constructor(scope) {
		this.scope = null;
		this.listeners = null;
		this.inDispatch = false;

		this.scope = scope || this;
		this.listeners = [];
	}

	/**
	 * Add an observer function to be executed when a dispatch call is done.
	 *
	 * @method add
	 * @param {function} callback Callback function to execute when a dispatch event occurs.
	 * @param {Object} s Optional execution scope, defaults to the one specified in the class constructor.
	 * @return {function} Returns the same function as the one passed on.
	 */
	add(callback, scope) {
		this.listeners.push({ cb: callback, scope: scope || this.scope });

		return callback;
	}

	/**
	 * Add an observer function to be executed to the top of the list of observers.
	 *
	 * @method addToTop
	 * @param {function} callback Callback function to execute when a dispatch event occurs.
	 * @param {Object} scope Optional execution scope, defaults to the one specified in the class constructor.
	 * @return {function} Returns the same function as the one passed on.
	 */
	addToTop(callback, scope) {
		const self = this,
			listener = { cb: callback, scope: scope || self.scope };

		// Create new listeners if addToTop is executed in a dispatch loop
		if (self.inDispatch) {
			self.listeners = [listener].concat(self.listeners);
		} else {
			self.listeners.unshift(listener);
		}

		return callback;
	}

	/**
	 * Removes an observer function.
	 *
	 * @method remove
	 * @param {function} callback Observer function to remove.
	 * @return {function} The same function that got passed in or null if it wasn't found.
	 */
	remove(callback) {
		const listeners = this.listeners;
		let output = null;

		window.tinymce.each(listeners, (listener, i) => {
			if (callback == listener.cb) {
				output = listener;
				listeners.splice(i, 1);
				return false;
			}
		});

		return output;
	}

	/**
	 * Dispatches an event to all observers/listeners.
	 *
	 * @method dispatch
	 * @param {Object} .. Any number of arguments to dispatch.
	 * @return {Object} Last observer functions return value.
	 */
	dispatch() {
		const self = this,
			args = arguments,
			listeners = self.listeners;
		let listener, i, returnValue;

		self.inDispatch = true;

		// Needs to be a real loop since the listener count might change while looping
		// And this is also more efficient
		for (i = 0; i < listeners.length; i++) {
			listener = listeners[i];
			returnValue = listener.cb.apply(listener.scope, args.length > 0 ? args : [listener.scope]);

			if (returnValue === false) {
				break;
			}
		}

		self.inDispatch = false;

		return returnValue;
	}

	/**#@-*/
}

function isFormatOperation(operation) {
	return typeof operation === 'object' && operation.name;
}

function queryCommandState(editor, cmd) {
	// Some of the cmd:s are functions passed in
	if (typeof cmd === 'string') {
		return editor.queryCommandState(cmd);
	} else if (isFormatOperation(cmd)) {
		//This is likely a formatting operation.
		return editor.queryCommandState(cmd.name);
	} else {
		// We don't know what this is.
		return false;
	}
}

function execCommand(editor, cmd, ui, value) {
	return editor.execCommand(cmd, ui, value);
}

export function AtlassianTheme() {
	/**
	 * Represents the page editor (which renders the page title input field over the tinymce
	 * iframe).
	 *
	 * @param ed - Tinymce editor instance
	 * @constructor
	 */
	function PageEditor(ed) {
		// Guarantees that we run setup and init exactly once per instance as we replace
		// the function definitions with their boolean results
		this.init = this.init(ed);
	}

	/**
	 * This is a list of TinyMCE plugins that are incompatible with the page editor theme - they will
	 * be suppressed when the theme is initialised. Please note that this should only be used to
	 * blacklist plugins that we have no control over (e.g. third-party plugins). In other cases,
	 * AJS.Confluence.Editor._Profiles should be used to control which editor plugins are loaded
	 * based on the editing context.
	 */
	PageEditor.INCOMPATIBLE_PLUGINS = [
		'dfe', // 'Distraction free editor' that we used to bundle (com.atlassian.confluence.plugins.confluence-editor-hide-tools)
	];

	PageEditor.shouldInit = () => {
		const $body = $('body');
		return $body.hasClass('edit') || $body.hasClass('create') || $body.hasClass('copy-page');
	};

	PageEditor.suppressIncompatiblePlugins = () => {
		const noop = () => {};
		for (let i = 0, len = PageEditor.INCOMPATIBLE_PLUGINS.length; i < len; i++) {
			window.tinymce.PluginManager.lookup[PageEditor.INCOMPATIBLE_PLUGINS[i]] = noop;
		}
	};

	PageEditor.prototype.init = (ed) => {
		const keyCodeManager = $.ui.keyCode;
		const $contentTitle = $('#content-title');
		const $contentWrap = $('form.editor');
		const $editorBody = $(ed.getBody());
		const $editorDoc = $(ed.getDoc());
		const $editorPrecursor = $('#editor-precursor');
		const $editorWindow = $(ed.getWin());
		const $toolbar = $('#toolbar');
		const $primaryToolbar = $('#toolbar .toolbar-primary');

		const initialScroll = $editorDoc.scrollTop();
		const precursorTopPadding = parseInt($editorPrecursor.css('padding-top'), 10);

		const animationFrame = window.requestAnimationFrame || window.webkitRequestAnimationFrame;

		const $breadcrumbAnchor = $('<ol id="breadcrumbs"></ol>');
		const $cell = $('.cell', $editorPrecursor);
		$cell.prepend($breadcrumbAnchor);

		function startEditorPrecursorRepositionFrame(animationFrame) {
			/*
             The toolbar above the precursor has contextual sections that animate
             without triggering events that we can listen for (e.g. find and replace).
             */
			(function animloop() {
				animationFrame(animloop);
				reposition();
			})();

			/*
             CONFDEV-27352 - repositioning of precursor is delayed when scrolling with the
             trackpad in Safari if we rely on request animation frame alone.

             This extra event handling has a low enough impact that we should apply it to all
             browsers (so that if it causes any problems in the future, we'll notice them sooner).
             */
			$editorBody.on('scroll mousewheel', () => {
				setTimeout(reposition, 0);
			});
		}

		function toggleToolBarDisabledState(state) {
			ed.plugins.customtoolbar.toggleToolbarButtons(state, state, state, () => {
				// filter to exclude close buttons.
				return !$(this).parent().hasClass('close');
			});
		}

		function setupTitleToolbar() {
			$contentTitle
				.on('keydown', (e) => {
					const code = e.keyCode || e.which;

					if (code === keyCodeManager.ENTER || code === keyCodeManager.DOWN) {
						e.preventDefault();
						e.stopPropagation();
						ed.focus();
					}
				})
				.on('focus click', () => {
					// When title receives focus, toolbar buttons should be disabled until user focus on the editor again.
					toggleToolBarDisabledState(true);
				});

			$editorWindow.on('focus', () => {
				toggleToolBarDisabledState(false);
			});
		}
		function adg3Reposition() {
			const amountOfIframeScroll = initialScroll - $editorDoc.scrollTop();
			const heightAboveToolbar = $toolbar.offset().top - $contentWrap.offset().top; // e.g. merge conflict resolution
			const heightOfPrimaryToolbar = $primaryToolbar.outerHeight();
			const precursorTopMargin = parseInt($editorPrecursor.css('margin-top'), 10);
			const precursorHeight = $editorPrecursor.outerHeight();

			$editorBody.css('padding-top', `${precursorTopMargin + precursorHeight}px`);
			$editorPrecursor.css(
				'top',
				`${
					heightAboveToolbar + heightOfPrimaryToolbar + amountOfIframeScroll + precursorTopPadding
				}px`,
			);
		}
		const reposition = adg3Reposition;

		setupTitleToolbar();
		startEditorPrecursorRepositionFrame(animationFrame);

		return true;
	};

	const ConfluenceThemeOptions = {
		controlHandlers: {},

		controls: {
			bold: 'Bold',
			italic: 'Italic',
			underline: 'Underline',
			strikethrough: 'Strikethrough',
			justifyleft: 'JustifyLeft',
			justifycenter: 'JustifyCenter',
			justifyright: 'JustifyRight',
			bullist: 'InsertUnorderedList',
			numlist: 'InsertOrderedList',
			tasklist: 'InsertInlineTaskList',
			outdent: 'Outdent',
			indent: 'Indent',
			cut: 'Cut',
			copy: 'Copy',
			paste: 'Paste',
			undo: 'Undo',
			redo: 'Redo',
			removeformat: 'RemoveFormat',
			sub: 'subscript',
			sup: 'superscript',
			forecolor: 'ForeColor',
			forecolorpicker: 'mceForeColor',
			backcolor: 'HiliteColor',
			backcolorpicker: 'mceBackColor',
			charmap: 'mceCharMap',
			visualaid: 'mceToggleVisualAid',
			anchor: 'mceInsertAnchor',
			newdocument: 'mceNewDocument',
			blockquote: 'mceBlockQuote',
		},

		//things whose state needs to be reflected in the toolbar (active inactive)
		stateControls: [
			'bold',
			'italic',
			'underline',
			'bullist',
			'numlist',
			'tasklist',
			'justifyleft',
			'justifycenter',
			'justifyright',
			'strikethrough',
			'sub',
			'sup',
			'monospace',
		],

		init(ed, url) {
			const shouldInitPageEditor = PageEditor.shouldInit();
			const t = this;

			shouldInitPageEditor && PageEditor.suppressIncompatiblePlugins();

			t.editor = ed;
			t.url = url;

			// Default settings
			t.settings = window.tinymce.extend(
				{
					theme_advanced_toolbar_location: 'bottom',
					theme_advanced_buttons1:
						'formatselect,bold,italic,underline,strikethrough,forecolor,separator,' +
						'table,row_before,row_after,delete_row,col_before,col_after,delete_col,delete_table,separator,' +
						'bullist,numlist,tasklist,outdent,indent,blockquote,justifyleft,justifycenter,justifyright,justifyfull,separator,sup,sub,separator,' +
						'undo,redo,separator,' +
						'conflink,oldlinkbrowserButton,confimage,conf_macro_browser,separator,' +
						'search,code,customtoolbar, monospace' +
						'p,h1,h2,h3,h4,h5,h6,pre,blockquote',
					readonly: ed.settings.readonly,
				},
				ed.settings,
			);

			// Init editor
			ed.on('init', () => {
				if (!ed.settings.readonly) {
					ed.on('NodeChange', (e) => {
						t.nodeChanged(ed, null, e.element, false, { parents: e.parents });
					});
					ed.on('keyup', () => {
						t._updateUndoStatus(ed);
					});
					ed.on('MouseUp', () => {
						t._updateUndoStatus(ed);
					});
					ed.dom.bind(ed.dom.getRoot(), 'dragend', () => {
						t._updateUndoStatus(ed);
					});

					shouldInitPageEditor && new PageEditor(ed);
				}
			});
		},

		execCommand(cmd, ui, val) {
			const f = this[`_${cmd}`];

			if (f) {
				f.call(this, ui, val);
				return true;
			}

			return false;
		},

		renderUI(o) {
			let sc;
			this.bindControls();
			return {
				iframeContainer: window.tinymce.DOM.get('rte'),
				editorContainer: 'wysiwyg',
				sizeContainer: sc,
				deltaHeight: o.deltaHeight,
			};
		},

		getInfo() {
			return {
				longname: 'Confluence theme',
				author: 'Atlassian',
				authorurl: 'http://tinymce.moxiecode.com',
				version: `${window.tinymce.majorVersion}.${window.tinymce.minorVersion}`,
			};
		},

		/**
		 * Binds event handlers for each theme_advanced_button.
		 *
		 * @param toolBar String representing the toolbar (ex. "formatDropdown, bold, italic, underline")
		 */
		bindControls() {
			const t = this;
			const s = t.settings;

			// Register dispatchers that toolbar uses to show button state
			window.tinymce.each(window.tinymce.explode(s['theme_advanced_buttons1']), (element) => {
				//things added via editor.addButton()
				if (t.createEditorButtons(element)) {
					return;
				}

				//special cases handled by their own methods
				switch (element) {
					case 'formatselect':
						t.createBlockFormats();
				}
			});
			t.bindControlHandlers();
			t.renderToolbarButtons();
		},

		/**
		 * Render all buttons in registered button list ("editor.buttons") in toolbars.
		 * Just render button which specify "locationGroup".
		 * If a button does not specify "weight", it will be appended at the last of button group.
		 * If weight of button is 0, it will be added at the beginning of button group.
		 * @example
		 * //Adding button
		 * ed.addButton('confluence-insert-files', {
		 *     //default value of "toolbar" is "toolbar-primary"
		 *     title : AJS.I18n.getText("tinymce.confluence.conf_file"),
		 *     tooltip: AJS.I18n.getText("tinymce.confluence.conf_file_desc"),
		 *     cmd: "mceConfimage",
		 *     className: "insert-files",
		 *     locationGroup: "rte-toolbar-group-link",
		 *     weight: 1 //weight of the new button in group
		 * });
		 */
		renderToolbarButtons() {
			const t = this;
			// This comes from Confluence Templates.Editor.Toolbar.toolbarButton;
			function templateToolbarButton(button) {
				return `<li id="${button.id}" class="toolbar-item ${button.className}" data-tooltip="${button.tooltip}" data-weight="${button.weight}">
                            <a class="toolbar-trigger aui-button" href="#" data-control-id="${button.id}">
                                <span class="icon ${button.icon}"></span>
                                <span class="trigger-text">${button.title}</span>
                            </a>
                        </li>`;
			}
			const SUPPORTED_TOOLBARS = ['toolbar-primary'];
			const DEFAULT_BUTTON_SETTING = {
				toolbar: 'toolbar-primary',
				title: '',
				tooltip: '',
				cmd: '',
				className: '',
				locationGroup: null,
				weight: Infinity,
				onclick: null,
			};

			//"editor.buttons" is a map, so convert a map to list for sorting easily
			let listButtons = map(this.editor.buttons, (btnSetting, btnKey) => {
				btnSetting = Object.assign({}, DEFAULT_BUTTON_SETTING, btnSetting);
				//keep track id of button into its setting
				btnSetting.id = btnKey;
				btnSetting.weight = isNumber(btnSetting.weight) ? btnSetting.weight : Infinity;
				return btnSetting;
			});

			//just render button which has "locationGroup" property,
			listButtons = filter(listButtons, (btnSetting) => {
				return !!btnSetting.locationGroup;
			});

			loEach(SUPPORTED_TOOLBARS, (toolbar) => {
				const $toolbar = $(`.${toolbar}`);
				let currentButtons = filter(listButtons, { toolbar });

				currentButtons = sortBy(currentButtons, 'weight');

				loEach(currentButtons, (btnSetting) => {
					t.createEditorButtons(btnSetting.id);
					const $group = $toolbar.find(`.${btnSetting.locationGroup}`);
					const $button = $(templateToolbarButton(btnSetting));

					$group.append($button).removeClass('hidden');
				});
			});
		},

		createEditorButtons(buttonName) {
			const button = this.editor.buttons[buttonName];
			if (button) {
				if (button.cmd) {
					this.controls[buttonName] = button.cmd;
				} else {
					this.controls[buttonName] = {
						func: button.onclick,
						name: buttonName,
					};
				}
			} else {
				return false;
			}
		},

		bindControlHandlers() {
			const t = this;
			t.controlHandlers['buttons'] = {};
			t.controlHandlers['buttons']['state'] = new Dispatcher();
			t.controlHandlers['buttons']['click'] = new Dispatcher();
			t.controlHandlers['buttons']['click'].add((format) => {
				const controlAction = t.controls[format];
				const controlActionType = typeof controlAction;
				if (controlActionType === 'string') {
					execCommand(getRte().getEditor(), t.controls[format]);
				} else if (isFormatOperation(controlAction)) {
					controlAction.func();
				} else {
					// unsupported controlAction
				}
			});
		},

		createBlockFormats() {
			const t = this;
			t.controlHandlers['formatselect'] = {};
			t.controlHandlers['formatselect']['state'] = new Dispatcher();
			t.controlHandlers['formatselect']['click'] = new Dispatcher();
			t.controlHandlers['formatselect']['click'].add((format) => {
				execCommand(getRte().getEditor(), 'FormatBlock', false, format);
			});
		},

		createForeColorMenu() {
			return true;
		},

		_updateUndoStatus(ed) {
			const um = ed.undoManager;
			this.controlHandlers['buttons']['state'].dispatch(
				'undo',
				'disabled',
				!um.hasUndo() && !um.typing,
			);
			this.controlHandlers['buttons']['state'].dispatch('redo', 'disabled', !um.hasRedo());
		},

		nodeChanged(ed, cm, n, co, ob) {
			const t = this;
			window.tinymce.each(t.stateControls, (buttonName) => {
				t.controlHandlers['buttons']['state'].dispatch(
					buttonName,
					'active',
					!!queryCommandState(ed, t.controls[buttonName]),
				);
			});

			function getParent(func) {
				const parents = ob.parents;
				for (let i = 0; i < parents.length; i++) {
					if (func(parents[i])) {
						return parents[i];
					}
				}
			}

			t._updateUndoStatus(ed);
			t.controlHandlers['buttons']['state'].dispatch(
				'outdent',
				'disabled',
				!queryCommandState(ed, 'Outdent'),
			);

			const p = getParent(ed.dom.isBlock);
			if (p) {
				t.controlHandlers['formatselect']['state'].dispatch(p.nodeName.toLowerCase());
			}
		},
	};

	return {
		Editor: PageEditor,
		ThemeOptions: ConfluenceThemeOptions,
	};
}
